import { useState, useEffect } from 'react';
// store
import { useSelector, useDispatch, SET_DEVICE } from '../../../store';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// @devextreme
import DateBox from 'devextreme-react/date-box';
import DateRangeBox from 'devextreme-react/date-range-box';
import { Template } from 'devextreme-react/core/template';
import { Popover } from 'devextreme-react/popover';
import { exportDataGrid } from 'devextreme/excel_exporter';
// @devextreme -- export to excel
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver-es';
// @moment
import moment from 'moment-timezone';
// utils
import { timezones } from '../../../utils/format';
// assets
import { ReactComponent as Download } from '../../../assets/button/download.svg';
import Calendar from '../../../assets/button/Calendar';
// styles
import { SearchWrapper, Tabs, PrimaryButton, IconButton, DateButton, MobileDateWrapper, SelectButton } from '../../../styles';
// apis
import { downloadInquiry } from '../../../apis';

// ----------------------------------------------------------------------

interface Props {
  interval: string;
  setInterval: React.Dispatch<React.SetStateAction<string>>;
  start: Date;
  setStart: React.Dispatch<React.SetStateAction<Date>>;
  end: Date;
  setEnd: React.Dispatch<React.SetStateAction<Date>>;
  searched: any;
  handleSearch: () => Promise<void>
  gridRef: React.MutableRefObject<any>;
  setMessage: React.Dispatch<any>;
};

const now = timezones(moment().utc().format(), 'YYYY-MM-DDTHH:mm:ss');
const intervals = [{ value: 'daily', name: '일간' }, { value: 'weekly', name: '주간' }, { value: 'monthly', name: '월간' }, { value: 'setting', name: '직접 설정' }];

// ----------------------------------------------------------------------

export default function SearchContent({ interval, setInterval, start, setStart, end, setEnd, searched, handleSearch, gridRef, setMessage }: Props) {
  const mobile = useResponsive('down', 'md');
  const dispatch = useDispatch();

  const device = useSelector((state) => state.device);
  const devices = useSelector((state) => state.devices);

  const [ display, setDisplay ] = useState('yyyy-MM-dd');

  const [ saveStart, setSaveStart ] = useState<any>({ daily: new Date(now), weekly: new Date(now), monthly: new Date(now), setting: new Date(now) });
  const [ saveEnd, setSaveEnd ] = useState<any>({ daily: new Date(now), weekly: new Date(now), monthly: new Date(now), setting: new Date(now) });

  const [ range, setRange ] = useState(false);

  const [ visible, setVisible ] = useState(false);

  const [ visibleDevice, setVisibleDevice ] = useState(false);

  const handleSelection = (value: any) => {
    dispatch( SET_DEVICE(value) );
    
    setVisibleDevice(false);
  };

  useEffect(() => {
    setSaveStart({ ...saveStart, [interval]: start});
  }, [start]);

  useEffect(() => {
    setSaveEnd({ ...saveEnd, [interval]: end});
  }, [end]);

  useEffect(() => {
    if ( interval === 'weekly' ) {
      handleWeekly(saveStart.weekly);
    } else {
      setStart(saveStart[interval]);
      setEnd(saveEnd[interval]);
    }
    
    if ( interval === 'monthly' ) {
      document.body.className  += ' active';
    } else {
      document.body.classList.remove('active');
    }

    switch ( interval ) {
      case 'monthly':
        return setDisplay('yyyy-MM');
      default:
        return setDisplay('yyyy-MM-dd');
    }
  }, [interval]);

  const handleWeekly = (value: Date) => {
    const current = new Date(value);
    const startDate = new Date(current.setDate(current.getDate() - current.getDay()));
    const sunday = new Date(startDate);
    let endDate = new Date(sunday.setDate(sunday.getDate() + 6));
    if ( endDate.getTime() > new Date(now).getTime() ) endDate = new Date(now);

    setStart(new Date(startDate.getTime()));
    setEnd(new Date(endDate.getTime()));
  };

  const onValueChange = (event: any) => {
    if ( !mobile ) {
      const previous = event.previousValue;
      const values = event.value;
      const filter = values.filter((value: string) => !previous.includes(value));
      const value = filter.length > 0 ? filter[0] : null;
  
      const startDate = new Date(previous[0]).getTime();
      const endDate = new Date(previous[1]).getTime();
      const currentDate = new Date(value).getTime();
  
      if ( !value || ( currentDate >= startDate && currentDate <= endDate ) ) return;
  
      setRange(false);
      handleWeekly(value);
      document.getElementById('rangeStart').blur();
      setTimeout(() => {
        document.getElementById('rangeEnd').blur();
      }, 5);
    } else {
      handleWeekly(event.value);
    }
  };

  const handleDate = (type: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    switch ( interval ) {
      case 'daily':
        if ( type !== 'previous' && moment(startDate).isSameOrAfter(now, 'day') ) return;
        const date = type === 'previous' ? startDate.setDate(startDate.getDate() - 1) : startDate.setDate(startDate.getDate() + 1);
        return setStart(new Date(date));
      case 'weekly':
        const date2 = type === 'previous' ? startDate.setDate(startDate.getDate() - 1) : endDate.setDate(endDate.getDate() + 1);
        return handleWeekly(new Date(date2));
      default:
        if ( type !== 'previous' && moment(startDate).isSameOrAfter(now, 'month') ) return;
        const date3 = type === 'previous' ? startDate.setMonth(startDate.getMonth() - 1) : startDate.setMonth(startDate.getMonth() + 1);
        setStart(new Date(date3));
    }
  };

  const handleDownload = async (value: string) => {
    if ( value === 'current' ) {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet('Main sheet');
      exportDataGrid({ worksheet, component: gridRef.current.instance })
        .then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `data_inquiry-${timezones(Date.now(), 'YYYYMMDDHHmmss')}.csv`);
          });
        });
    } else {
      await downloadInquiry(device.iotDeviceId, searched.start, searched.end, searched.interval, value === 'original' ? true : false)
        .catch(() => setMessage('다운로드를 할 수 없습니다.\n나중에 다시 시도해 주세요.'));
    }
  };

  return (
    <>
      <SearchWrapper>
        <div>
          { mobile &&
            <SelectButton type='button' id='device' onClick={() => setVisibleDevice(true)} disabled={!device ? true : false}>{ device?.location }<div/></SelectButton>
          }
          <Popover
            target='#device'
            visible={visibleDevice}
            hideOnOutsideClick={true}
            onHiding={() => setVisibleDevice(false)}
            wrapperAttr={{'class': 'mobilePopover'}}
          >
            { devices.map((item: any) => (
              <button type='button' key={item.iotDeviceId} onClick={() => handleSelection(item)}>
                { item.location }
              </button>
            ))}
          </Popover>

          <Tabs>
            { intervals.map((item) => (
              <button key={item.value} onClick={() => setInterval(item.value)} className={interval === item.value ? 'active' : ''}>{item.name}</button>
            ))}
          </Tabs>

          <MobileDateWrapper>
            { interval !== 'setting' &&
              <DateButton onClick={() => handleDate('previous')} />
            }

            { interval === 'weekly' && !mobile &&
              <DateRangeBox
                opened={range}
                value={[start, end]}
                onValueChanged={onValueChange}
                onFocusIn={() => setRange(true)}
                onFocusOut={() => setRange(false)}
                displayFormat={display}
                openOnFieldClick={true}
                useMaskBehavior={true}
                startDateLabel=''
                endDateLabel=''
                startDateInputAttr={{id: 'rangeStart'}}
                endDateInputAttr={{id: 'rangeEnd'}}
                max={new Date(now)}
                dropDownButtonTemplate='calendar'
              >
                <Template name='calendar' render={Calendar} />
              </DateRangeBox>
            }

            { interval === 'weekly' && mobile &&
              <div className='rangeBox'>
                <DateBox
                  type='date'
                  value={start}
                  onValueChanged={onValueChange}
                  displayFormat={display}
                  elementAttr={{class: 'calendarStart'}}
                  openOnFieldClick={true}
                  useMaskBehavior={true}
                  pickerType='rollers'
                  max={new Date(now)}
                />
                <p className='range'>~</p>
                <DateBox
                  type='date'
                  value={end}
                  onValueChanged={onValueChange}
                  displayFormat={display}
                  elementAttr={{class: 'calendarEnd'}}
                  openOnFieldClick={true}
                  useMaskBehavior={true}
                  pickerType='rollers'
                  max={new Date(now)}
                  dropDownButtonTemplate='calendar'
                >
                  <Template name='calendar' render={Calendar} />
                </DateBox>
              </div>
            }

            { interval !== 'weekly' && interval !== 'setting' &&
              <DateBox
                type={ interval === 'setting' ? 'datetime' : 'date' }
                value={start}
                onValueChanged={(event) => setStart(event.value)}
                displayFormat={display}
                calendarOptions={{
                  maxZoomLevel: interval === 'monthly' ? 'year' : 'month',
                  minZoomLevel: 'century'
                }}
                openOnFieldClick={true}
                useMaskBehavior={true}
                pickerType={mobile ? 'rollers' : 'calendar'}
                max={new Date(now)}
                dropDownButtonTemplate='calendar'
              >
                <Template name='calendar' render={Calendar} />
              </DateBox>
            }

            { interval !== 'setting' &&
              <DateButton onClick={() => handleDate('next')} $next />
            }

            { interval === 'setting' && !mobile &&
              <DateRangeBox
                value={[start, end]}
                onValueChanged={(event) => {
                  setStart(event.value[0]);
                  setEnd(event.value[1]);
                }}
                displayFormat='yyyy-MM-dd'
                openOnFieldClick={true}
                useMaskBehavior={true}
                startDateLabel=''
                endDateLabel=''
                max={new Date(now)}
                dropDownButtonTemplate='calendar'
              >
                <Template name='calendar' render={Calendar} />
              </DateRangeBox>
            }

            { interval === 'setting' && mobile &&
              <div className='rangeBox' style={{'width': '100%'}}>
                <DateBox
                  type='date'
                  value={start}
                  onValueChanged={(event) => setStart(event.value)}
                  displayFormat={display}
                  elementAttr={{class: 'calendarStart'}}
                  openOnFieldClick={true}
                  useMaskBehavior={true}
                  pickerType='rollers'
                  max={new Date(now)}
                />
                <p className='range'>~</p>
                <DateBox
                  type='date'
                  value={end}
                  onValueChanged={(event) => setEnd(event.value)}
                  displayFormat={display}
                  elementAttr={{class: 'calendarEnd'}}
                  openOnFieldClick={true}
                  useMaskBehavior={true}
                  pickerType='rollers'
                  max={new Date(now)}
                  dropDownButtonTemplate='calendar'
                >
                  <Template name='calendar' render={Calendar} />
                </DateBox>
              </div>
            }
          </MobileDateWrapper>

          <PrimaryButton type='button' onClick={() => handleSearch()} disabled={!device?.iotDeviceId ? true : false}>검색</PrimaryButton>
        </div>

        <IconButton type='button' id='download' onClick={() => setVisible(true)} disabled={!device?.iotDeviceId ? true : false}><Download />다운로드</IconButton>
      </SearchWrapper>
      <Popover
        target='#download'
        visible={visible}
        hideOnOutsideClick={true}
        onHiding={() => setVisible(false)}
      >
        <button type='button' onClick={() => handleDownload('current')}>현재 페이지</button>
        <button type='button' onClick={() => handleDownload('original')}>원본 데이터</button>
        <button type='button' onClick={() => handleDownload('analysis')}>분석용 데이터</button>
      </Popover>
    </>
  );
}